<template>
  <div class="container-fluid py-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
      <div>
        <h3 class="mb-1">Faktury</h3>
        <p class="text-sm text-muted mb-0">
          Centralny rejestr faktur ze wszystkich sklepów i źródeł.
        </p>
      </div>

      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-primary mb-0"
          :disabled="loading"
          @click="loadInvoices"
        >
          <i class="fas fa-sync-alt me-2"></i>
          Odśwież
        </button>

        <button
          class="btn btn-primary mb-0"
          @click="openOrderModal"
        >
          <i class="fas fa-plus me-2"></i>
          Wystaw z zamówienia
        </button>
      </div>
    </div>

    <!-- Wyszukiwanie i filtry -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12 col-lg-4">
            <label class="form-label">Szukaj</label>
            <input
              v-model="filters.search"
              type="search"
              class="form-control"
              placeholder="Numer faktury, klient, NIP, zamówienie..."
              @keyup.enter="applyFilters"
            />
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label">Sklep</label>
            <select v-model="filters.shopId" class="form-select">
              <option value="">Wszystkie</option>
              <option
                v-for="shop in shops"
                :key="shop.id"
                :value="String(shop.id)"
              >
                {{ shop.name }}
              </option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label">Źródło</label>
            <select v-model="filters.source" class="form-select">
              <option value="">Wszystkie</option>
              <option value="MANUAL">Ręczne</option>
              <option value="WEBSITE">Sklep internetowy</option>
              <option value="ALLEGRO">Allegro</option>
              <option value="OTHER">Inne</option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label">Status</label>
            <select v-model="filters.status" class="form-select">
              <option value="">Wszystkie</option>
              <option value="DRAFT">Robocza</option>
              <option value="ISSUED">Wystawiona</option>
              <option value="PAID">Opłacona</option>
              <option value="CANCELLED">Anulowana</option>
              <option value="CORRECTED">Skorygowana</option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label">Data od</label>
            <input v-model="filters.dateFrom" type="date" class="form-control" />
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label">Data do</label>
            <input v-model="filters.dateTo" type="date" class="form-control" />
          </div>

          <div class="col-12 col-lg-10 d-flex align-items-end gap-2">
            <button
              class="btn btn-primary mb-0"
              :disabled="loading"
              @click="applyFilters"
            >
              <i class="fas fa-search me-2"></i>
              Szukaj
            </button>

            <button
              class="btn btn-outline-secondary mb-0"
              :disabled="loading"
              @click="clearFilters"
            >
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Komunikaty -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <div v-if="success" class="alert alert-success" role="alert">
      {{ success }}
    </div>

    <!-- Tabela -->
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Rejestr faktur</h6>
          <span class="text-sm text-muted">
            Liczba wyników: {{ totalCount }}
          </span>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th>Numer</th>
              <th>Data</th>
              <th>Klient</th>
              <th>Sklep / źródło</th>
              <th>Zamówienie</th>
              <th class="text-end">Kwota</th>
              <th>Status</th>
              <th class="text-end">Akcje</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-5">
                <span class="spinner-border spinner-border-sm me-2"></span>
                Ładowanie faktur...
              </td>
            </tr>

            <tr v-else-if="invoices.length === 0">
              <td colspan="8" class="text-center py-5 text-muted">
                Brak faktur spełniających kryteria.
              </td>
            </tr>

            <tr v-for="invoice in invoices" :key="invoice.Id ?? invoice.id">
              <td>
                <span class="text-sm fw-bold">
                  {{ invoice.InvoiceNumber }}
                </span>
                <div
                  v-if="getInFaktStatus(invoice)"
                  class="mt-1"
                >
                  <span
                    class="badge"
                    :class="inFaktStatusClass(getInFaktStatus(invoice).status)"
                  >
                    {{ getInFaktStatus(invoice).status || "Status nieznany" }}
                  </span>

                  <div
                    v-if="getInFaktStatus(invoice).message"
                    class="text-xs text-muted mt-1"
                  >
                    {{ getInFaktStatus(invoice).message }}
                  </div>

                  <div
                    v-if="getInFaktStatus(invoice).checkedAt"
                    class="text-xs text-muted"
                  >
                    Sprawdzono: {{ formatDateTime(getInFaktStatus(invoice).checkedAt) }}
                  </div>
                </div>
              </td>

              <td class="text-sm">
                {{ formatDate(invoice.InvoiceDate) }}
              </td>

              <td>
                <div class="text-sm fw-bold">
                  {{ invoice.BuyerName || invoice.BuyerCompanyName || "—" }}
                </div>
                <div v-if="invoice.BuyerTaxId" class="text-xs text-muted">
                  NIP: {{ invoice.BuyerTaxId }}
                </div>
              </td>

              <td>
                <div class="text-sm">{{ getShopName(invoice.ShopId) }}</div>
                <div class="text-xs text-muted">
                  {{ sourceLabel(invoice.Source) }}
                </div>
              </td>

              <td>
                <span class="text-sm">
                  {{ invoice.ExternalOrderNumber || invoice.SalesOrderId || "—" }}
                </span>
              </td>

              <td class="text-end text-sm fw-bold">
                {{ formatMoney(invoice.TotalGross, invoice.Currency) }}
              </td>

              <td>
                <span class="badge" :class="statusClass(invoice.Status)">
                  {{ statusLabel(invoice.Status) }}
                </span>
              </td>

              <td class="text-end">
                <div class="d-inline-flex gap-1">
                  
                  <button
                    class="btn btn-sm btn-outline-secondary mb-0"
                    title="Podgląd"
                    @click="showPreview(invoice)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>

                  <button
                    class="btn btn-sm btn-outline-primary mb-0"
                    title="PDF"
                    :disabled="busyPdfId === (invoice.Id ?? invoice.id)"
                    @click="openPdf(invoice)"
                  >
                    <i class="fas fa-file-pdf"></i>
                  </button>
                  <button
                    v-if="isDraft(invoice)"
                    class="btn btn-sm btn-outline-danger mb-0"
                    title="Usuń fakturę roboczą"
                    :disabled="busyDeleteId === getInvoiceId(invoice)"
                    @click="deleteInvoice(invoice)"
                  >
                    <span
                      v-if="busyDeleteId === getInvoiceId(invoice)"
                      class="spinner-border spinner-border-sm"
                    ></span>

                    <i v-else class="fas fa-trash"></i>
                  </button>
                  <button
                    v-if="isDraft(invoice)"
                    class="btn btn-sm btn-success mb-0"
                    title="Wystaw fakturę"
                    :disabled="busyIssueId === (invoice.Id ?? invoice.id)"
                    @click="issueInvoice(invoice)"
                  >
                    <i class="fas fa-check me-1"></i>
                    Wystaw
                  </button>

                  <button
                    v-if="isIssued(invoice)"
                    class="btn btn-sm btn-outline-info mb-0"
                    title="Wyślij do inFakt"
                    :disabled="busySyncId === getInvoiceId(invoice)"
                    @click="syncInvoiceWithInFakt(invoice)"
                  >
                    <span
                      v-if="busySyncId === getInvoiceId(invoice)"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <i v-else class="fas fa-cloud-upload-alt"></i>
                  </button>

                  <button
                    v-if="isIssued(invoice)"
                    class="btn btn-sm btn-outline-dark mb-0"
                    title="Sprawdź aktualny status w inFakt"
                    :disabled="busyStatusId === getInvoiceId(invoice)"
                    @click="checkInFaktStatus(invoice)"
                  >
                    <span
                      v-if="busyStatusId === getInvoiceId(invoice)"
                      class="spinner-border spinner-border-sm"
                    ></span>

                    <i v-else class="fas fa-sync-alt"></i>
                  </button>

                  
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-footer d-flex flex-wrap justify-content-between align-items-center">
        <span class="text-sm text-muted">
          Strona {{ page }} z {{ pageCount }}
        </span>

        <div class="d-flex gap-2">
          <button
            class="btn btn-sm btn-outline-secondary mb-0"
            :disabled="page <= 1 || loading"
            @click="changePage(page - 1)"
          >
            Poprzednia
          </button>
          <button
            class="btn btn-sm btn-outline-secondary mb-0"
            :disabled="page >= pageCount || loading"
            @click="changePage(page + 1)"
          >
            Następna
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: wybór zamówienia -->
    <div
      v-if="orderModalOpen"
      class="modal-backdrop-custom"
      @click.self="orderModalOpen = false"
    >
      <div class="modal-card card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Wystaw fakturę z zamówienia</h5>
          <button class="btn-close" @click="orderModalOpen = false"></button>
        </div>

        <div class="card-body">
          <p class="text-sm text-muted">
            Podaj identyfikator zamówienia z SalesCenter.
            Faktura zostanie utworzona na podstawie danych zamówienia.
          </p>

          <label class="form-label">ID zamówienia</label>
          <input
            v-model.number="orderId"
            type="number"
            min="1"
            class="form-control"
            placeholder="Np. 123"
            @keyup.enter="createFromOrder"
          />

          <div v-if="orderError" class="alert alert-danger mt-3 mb-0">
            {{ orderError }}
          </div>
        </div>

        <div class="card-footer d-flex justify-content-end gap-2">
          <button
            class="btn btn-outline-secondary mb-0"
            @click="orderModalOpen = false"
          >
            Anuluj
          </button>
          <button
            class="btn btn-primary mb-0"
            :disabled="creatingInvoice || !Number.isInteger(orderId) || orderId <= 0"
            @click="createFromOrder"
          >
            <span v-if="creatingInvoice" class="spinner-border spinner-border-sm me-2"></span>
            Utwórz fakturę
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: podgląd faktury -->
    <div
      v-if="previewOpen"
      class="modal-backdrop-custom"
      @click.self="previewOpen = false"
    >
      <div class="modal-card modal-card-lg card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0">
              {{ selectedInvoice?.invoiceNumber || "Podgląd faktury" }}
            </h5>
            <span class="text-sm text-muted">Podgląd danych dokumentu</span>
          </div>
          <button class="btn-close" @click="previewOpen = false"></button>
        </div>

        <div v-if="selectedInvoice" class="card-body">
          <div class="row g-4 mb-4">
            <div class="col-md-6">
              <h6>Sprzedawca</h6>
              <div>{{ selectedInvoice.sellerName }}</div>
              <div>{{ selectedInvoice.sellerTaxId }}</div>
              <div>{{ selectedInvoice.sellerAddress }}</div>
              <div>
                {{ selectedInvoice.sellerPostalCode }}
                {{ selectedInvoice.sellerCity }}
              </div>
            </div>

            <div class="col-md-6">
              <h6>Nabywca</h6>
              <div>{{ selectedInvoice.buyerCompanyName || selectedInvoice.buyerName }}</div>
              <div v-if="selectedInvoice.buyerTaxId">
                NIP: {{ selectedInvoice.buyerTaxId }}
              </div>
              <div>{{ selectedInvoice.buyerAddress }}</div>
              <div>
                {{ selectedInvoice.buyerPostalCode }}
                {{ selectedInvoice.buyerCity }}
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Lp.</th>
                  <th>Nazwa</th>
                  <th class="text-end">Ilość</th>
                  <th class="text-end">Cena jedn.</th>
                  <th class="text-end">Wartość</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in (selectedInvoice.items || [])"
                  :key="item.id || item.lineNumber"
                >
                  <td>{{ item.lineNumber }}</td>
                  <td>{{ item.productName }}</td>
                  <td class="text-end">{{ item.quantity }}</td>
                  <td class="text-end">
                    {{ formatMoney(item.unitNetPrice, selectedInvoice.currency) }}
                  </td>
                  <td class="text-end">
                    {{ formatMoney(item.grossAmount, selectedInvoice.currency) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-end">
            <div class="text-end">
              <div class="text-sm text-muted">Razem</div>
              <h4 class="mb-0">
                {{ formatMoney(selectedInvoice.totalGross, selectedInvoice.currency) }}
              </h4>
            </div>
          </div>

          <div v-if="selectedInvoice.vatExempt" class="alert alert-secondary mt-3 mb-0">
            Zwolnienie z VAT
            <span v-if="selectedInvoice.vatExemptionReason">
              — {{ selectedInvoice.vatExemptionReason }}
            </span>
          </div>
        </div>

        <div class="card-footer d-flex justify-content-end gap-2">
          <button
            class="btn btn-outline-primary mb-0"
            @click="openPdf(selectedInvoice)"
          >
            <i class="fas fa-file-pdf me-2"></i>PDF
          </button>
          <button
            v-if="selectedInvoice && isDraft(selectedInvoice)"
            class="btn btn-success mb-0"
            @click="issueInvoice(selectedInvoice)"
          >
            Wystaw fakturę
          </button>
          <button
            class="btn btn-secondary mb-0"
            @click="previewOpen = false"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import invoiceService from "@/services/invoiceService";

export default {
  name: "Invoices",

  data() {
    return {
      invoices: [],
      shops: [],
      totalCount: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      error: "",
      success: "",

      filters: {
        search: "",
        shopId: "",
        source: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      },

      orderModalOpen: false,
      orderId: null,
      creatingInvoice: false,
      orderError: "",

      previewOpen: false,
      selectedInvoice: null,

      busyPdfId: null,
      busyIssueId: null,
      busyDeleteId: null,
      busySyncId: null,
      busyStatusId: null,
      inFaktStatusByInvoiceId: {},
    };
  },

  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
  },

  created() {
    this.loadInvoices();
    this.loadShops();
  },

  methods: {
    async loadInvoices() {
      this.loading = true;
      this.error = "";

      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          search: this.filters.search || undefined,
          shopId: this.filters.shopId || undefined,
          source: this.filters.source || undefined,
          status: this.filters.status || undefined,
          dateFrom: this.filters.dateFrom || undefined,
          dateTo: this.filters.dateTo || undefined,
        };

        const response = await invoiceService.getInvoices(params);
        const envelope = response?.data ?? {};

        if (envelope.Success === false) {
          throw new Error(envelope.Message || "Nie udało się pobrać faktur.");
        }

        const data = envelope.Data ?? envelope.data ?? {};
        const items = Array.isArray(data)
          ? data
          : (data.Items ?? data.items ?? data.Items?.$values ?? data.items?.$values ?? []);

        this.invoices = items.map(item => this.addCamelCaseAliases(item));
        this.totalCount = Number(
          Array.isArray(data)
            ? items.length
            : (data.TotalCount ?? data.totalCount ?? data.Total ?? items.length)
        ) || 0;

        this.page = Number(data.Page ?? data.page ?? this.page) || 1;
        this.pageSize = Number(data.PageSize ?? data.pageSize ?? this.pageSize) || 20;
      } catch (e) {
        this.error = this.getErrorMessage(e, "Nie udało się pobrać faktur.");
      } finally {
        this.loading = false;
      }
    },

    async loadShops() {
      // Jeśli masz już shopService, zastąp ten fragment jego metodą.
      // Widok może działać bez listy sklepów; wtedy filtr Sklep pozostanie pusty.
      try {
        const response = await this.$axios?.get?.(
          `${process.env.VUE_APP_API_URL}Shop`
        );

        const responseData = response?.data;
        const data = responseData?.Data ?? responseData?.data ?? responseData;
        this.shops = (
          Array.isArray(data)
            ? data
            : (data?.Items ?? data?.items ?? data?.Items?.$values ?? [])
        ).map(shop => this.addCamelCaseAliases(shop));
      } catch {
        this.shops = [];
      }
    },

    applyFilters() {
      this.page = 1;
      this.loadInvoices();
    },

    clearFilters() {
      this.filters = {
        search: "",
        shopId: "",
        source: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      };
      this.page = 1;
      this.loadInvoices();
    },

    changePage(nextPage) {
      this.page = nextPage;
      this.loadInvoices();
    },

    openOrderModal() {
      this.orderId = null;
      this.orderError = "";
      this.orderModalOpen = true;
    },

    async createFromOrder() {
      if (!Number.isInteger(this.orderId) || this.orderId <= 0) {
        this.orderError = "Podaj prawidłowe ID zamówienia.";
        return;
      }

      this.creatingInvoice = true;
      this.orderError = "";
      this.error = "";
      this.success = "";

      try {
        await invoiceService.createFromOrder(this.orderId);
        this.orderModalOpen = false;
        this.success = "Faktura została utworzona.";
        this.page = 1;
        await this.loadInvoices();
      } catch (e) {
        console.log('Catch on createFromOrder',e)
        this.orderError = this.getErrorMessage(
          e,
          "Nie udało się utworzyć faktury z zamówienia."
        );
      } finally {
        this.creatingInvoice = false;
      }
      
    },

    async showPreview(invoice) {
      this.error = "";

      try {
        const invoiceId = invoice.Id ?? invoice.id;
        const response = await invoiceService.getInvoice(invoiceId);
        const payload = response?.data?.Data ?? response?.data?.data ?? response?.data;
        this.selectedInvoice = this.addCamelCaseAliases(payload);
        this.previewOpen = true;
      } catch (e) {
        this.error = this.getErrorMessage(e, "Nie udało się pobrać faktury.");
      }
    },

    async openPdf(invoice) {
      const invoiceId = invoice?.Id ?? invoice?.id;
      if (!invoiceId) return;

      this.busyPdfId = invoiceId;
      this.error = "";

      try {
        await invoiceService.openInvoicePdf(invoiceId);
      } catch (e) {
        this.error = this.getErrorMessage(e, "Nie udało się otworzyć PDF.");
      } finally {
        this.busyPdfId = null;
      }
    },

    async issueInvoice(invoice) {
      const invoiceId = invoice?.Id ?? invoice?.id;
      const invoiceNumber = invoice?.InvoiceNumber ?? invoice?.invoiceNumber;

      if (!invoiceId || !this.isDraft(invoice)) return;

      const confirmed = window.confirm(
        `Czy na pewno wystawić fakturę ${invoiceNumber || ""}?`
      );
      if (!confirmed) return;

      this.busyIssueId = invoiceId;
      this.error = "";
      this.success = "";

      try {
        await invoiceService.issueInvoice(invoiceId);

        this.success = `Faktura ${invoiceNumber || ""} została wystawiona.`;

        if ((this.selectedInvoice?.Id ?? this.selectedInvoice?.id) === invoiceId) {
          const response = await invoiceService.getInvoice(invoiceId);
          const payload = response?.data?.Data ?? response?.data?.data ?? response?.data;
          this.selectedInvoice = this.addCamelCaseAliases(payload);
        }

        await this.loadInvoices();
      } catch (e) {
        this.error = this.getErrorMessage(e, "Nie udało się wystawić faktury.");
      } finally {
        this.busyIssueId = null;
      }
    },

    addCamelCaseAliases(value) {
      if (Array.isArray(value)) {
        return value.map(item => this.addCamelCaseAliases(item));
      }

      if (!value || typeof value !== "object") return value;

      const result = {};
      for (const [key, item] of Object.entries(value)) {
        const normalizedValue = this.addCamelCaseAliases(item);
        result[key] = normalizedValue;

        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        if (!(camelKey in result)) {
          result[camelKey] = normalizedValue;
        }
      }

      return result;
    },

    isDraft(invoice) {
      const status = invoice?.Status ?? invoice?.status;
      return String(status).toUpperCase() === "DRAFT" || Number(status) === 1;
    },

    statusLabel(status) {
      const labels = {
        DRAFT: "Robocza",
        ISSUED: "Wystawiona",
        PAID: "Opłacona",
        CANCELLED: "Anulowana",
        CORRECTED: "Skorygowana",
        1: "Robocza",
        2: "Wystawiona",
        3: "Opłacona",
        4: "Anulowana",
        5: "Skorygowana",
      };

      return labels[String(status).toUpperCase()] || status || "—";
    },

    statusClass(status) {
      const value = String(status).toUpperCase();

      if (value === "ISSUED" || status === 2) return "bg-gradient-info";
      if (value === "PAID" || status === 3) return "bg-gradient-success";
      if (value === "CANCELLED" || status === 4) return "bg-gradient-secondary";
      if (value === "CORRECTED" || status === 5) return "bg-gradient-warning";

      return "bg-gradient-light text-dark";
    },

    sourceLabel(source) {
      const labels = {
        MANUAL: "Ręczne",
        WEBSITE: "Sklep internetowy",
        ALLEGRO: "Allegro",
        OTHER: "Inne",
        1: "Ręczne",
        2: "Sklep internetowy",
        3: "Allegro",
        4: "Inne",
      };

      return labels[String(source).toUpperCase()] || source || "—";
    },

    getShopName(shopId) {
      if (!shopId) return "—";

      const shop = this.shops.find(x => String(x.Id ?? x.id) === String(shopId));
      return shop?.Name ?? shop?.name ?? `Sklep #${shopId}`;
    },

    formatDate(value) {
      if (!value) return "—";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return new Intl.DateTimeFormat("pl-PL").format(date);
    },

    formatMoney(value, currency = "PLN") {
      return new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: currency || "PLN",
      }).format(Number(value || 0));
    },

    getErrorMessage(error, fallback) {
      console.log('errrrrooooorr:',error);
      return error?.response?.Data?.Message
        || error?.response?.Data?.Title
        || error?.Message
        || fallback;
    },

    getInvoiceId(invoice) {
      return invoice?.id ?? invoice?.Id ?? null;
    },

    async deleteInvoice(invoice) {
      const invoiceId = this.getInvoiceId(invoice);

      if (!invoiceId || !this.isDraft(invoice)) {
        return;
      }

      const invoiceNumber =
        invoice.invoiceNumber ?? invoice.InvoiceNumber ?? `ID ${invoiceId}`;

      const confirmed = window.confirm(
        `Czy na pewno usunąć fakturę roboczą ${invoiceNumber}?\n\n` +
        `Tej operacji nie można cofnąć.`
      );

      if (!confirmed) {
        return;
      }

      this.busyDeleteId = invoiceId;
      this.error = "";
      this.success = "";

      try {
        await invoiceService.deleteInvoice(invoiceId);

        this.success =
          `Faktura robocza ${invoiceNumber} została usunięta.`;

        // Zamknij podgląd, jeśli usuwana faktura jest aktualnie otwarta.
        if (
          this.getInvoiceId(this.selectedInvoice) === invoiceId
        ) {
          this.selectedInvoice = null;
          this.previewOpen = false;
        }

        // Odśwież rejestr.
        await this.loadInvoices();

        // Jeśli usunęliśmy ostatni rekord na stronie,
        // przejdź na poprzednią stronę.
        if (
          this.invoices.length === 0 &&
          this.page > 1
        ) {
          this.page -= 1;
          await this.loadInvoices();
        }
      } catch (e) {
        this.error = this.getErrorMessage(
          e,
          "Nie udało się usunąć faktury roboczej."
        );
      } finally {
        this.busyDeleteId = null;
      }
    },

    isIssued(invoice) {
      const status = invoice?.Status ?? invoice?.status;

      return (
        String(status).toUpperCase() === "ISSUED" ||
        Number(status) === 2
      );
    },

    async syncInvoiceWithInFakt(invoice) {
      const invoiceId = this.getInvoiceId(invoice);
      const invoiceNumber =
        invoice?.InvoiceNumber ??
        invoice?.invoiceNumber ??
        `ID ${invoiceId}`;

      if (!invoiceId || !this.isIssued(invoice)) {
        return;
      }

      const confirmed = window.confirm(
        `Czy wysłać fakturę ${invoiceNumber} do inFakt Sandbox?`
      );

      if (!confirmed) {
        return;
      }

      this.busySyncId = invoiceId;
      this.error = "";
      this.success = "";

      try {
        const response =
          await invoiceService.syncWithInFakt(invoiceId);

        const payload =
          response?.data?.Data ??
          response?.data?.data ??
          response?.data;

        this.success =
          payload?.Message ??
          payload?.message ??
          `Wysłano żądanie synchronizacji faktury ${invoiceNumber}.`;

        await this.loadInvoices();

        if (
          this.selectedInvoice &&
          this.getInvoiceId(this.selectedInvoice) === invoiceId
        ) {
          const details =
            await invoiceService.getInvoice(invoiceId);

          const invoiceData =
            details?.data?.Data ??
            details?.data?.data ??
            details?.data;

          this.selectedInvoice =
            this.addCamelCaseAliases(invoiceData);
        }
      } catch (e) {
        this.error = this.getErrorMessage(
          e,
          `Nie udało się wysłać faktury ${invoiceNumber} do inFakt.`
        );
      } finally {
        this.busySyncId = null;
      }
    },

    async checkInFaktStatus(invoice) {
      const invoiceId = this.getInvoiceId(invoice);

      if (!invoiceId) return;

      this.busyStatusId = invoiceId;
      this.error = "";

      try {
        const response =
          await invoiceService.refreshInFaktStatus(invoiceId);

        const payload =
          response?.data?.Data ??
          response?.data?.data ??
          response?.data;

        if (payload?.Success === false || payload?.success === false) {
          throw new Error(
            payload?.Message ||
            payload?.message ||
            "Nie udało się pobrać statusu z inFakt."
          );
        }

        const sync =
          payload?.Data ??
          payload?.data ??
          payload;

        this.inFaktStatusByInvoiceId = {
          ...this.inFaktStatusByInvoiceId,
          [invoiceId]: {
            status:
              sync?.ExternalStatus ??
              sync?.externalStatus ??
              sync?.Status ??
              sync?.status ??
              "Nieznany",

            message:
              sync?.Message ??
              sync?.message ??
              sync?.LastError ??
              sync?.lastError ??
              "",

            checkedAt: new Date().toISOString(),

            externalInvoiceNumber:
              sync?.ExternalInvoiceNumber ??
              sync?.externalInvoiceNumber ??
              null,

            externalInvoiceId:
              sync?.ExternalInvoiceId ??
              sync?.externalInvoiceId ??
              null,
          },
        };

        this.success =
          `Pobrano aktualny status synchronizacji faktury ${
            invoice.InvoiceNumber ?? invoice.invoiceNumber ?? invoiceId
          }.`;

        // Odśwież lokalne dane faktury, aby pobrać zapisany status synchronizacji.
        await this.loadInvoices();

      } catch (e) {
        this.error = this.getErrorMessage(
          e,
          "Nie udało się sprawdzić statusu faktury w inFakt."
        );
      } finally {
        this.busyStatusId = null;
      }
    },

    getInFaktStatus(invoice) {
      const invoiceId = this.getInvoiceId(invoice);
      return this.inFaktStatusByInvoiceId[invoiceId] ?? null;
    },

    inFaktStatusClass(status) {
      const value = String(status ?? "").toLowerCase();

      if (
        value.includes("zakończ") ||
        value.includes("utworzon") ||
        value.includes("sukces") ||
        value.includes("completed")
      ) {
        return "bg-gradient-success";
      }

      if (
        value.includes("błąd") ||
        value.includes("error") ||
        value.includes("failed")
      ) {
        return "bg-gradient-danger";
      }

      if (
        value.includes("oczek") ||
        value.includes("przyjęt") ||
        value.includes("pending") ||
        value.includes("progress")
      ) {
        return "bg-gradient-warning text-dark";
      }

      return "bg-gradient-secondary";
    },

    formatDateTime(value) {
      if (!value) return "—";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) return value;

      return new Intl.DateTimeFormat("pl-PL", {
        dateStyle: "short",
        timeStyle: "medium",
      }).format(date);
    },
  },
};

</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.55);
}

.modal-card {
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
}

.modal-card-lg {
  max-width: 900px;
}

.table th {
  white-space: nowrap;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.table td {
  vertical-align: middle;
}

.btn {
  white-space: nowrap;
}
</style>